import { useState, useEffect, useCallback, useRef } from 'react'
import { useSocket } from '../contexts/SocketContext'
import { apiGet } from '../utils/api'

export interface StatusCounts {
  all: number
  queued: number
  open: number
  closed: number
  missed: number
  auto_closed: number
  archived: number
}

/**
 * Lightweight hook that fetches conversation counts per status.
 * Updates in real time via socket events with debouncing to avoid 429 errors.
 */
export const useStatusCounts = (): StatusCounts => {
  const [counts, setCounts] = useState<StatusCounts>({ all: 0, queued: 0, open: 0, closed: 0, missed: 0, auto_closed: 0, archived: 0 })
  const { socket } = useSocket()
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchCounts = useCallback(async () => {
    try {
      const response = await apiGet<{ chats: any[]; total: number } | any[]>('/queue', { status: '' })
      // The /queue endpoint returns { queue: [...], queue_depth }. The previous
      // code only looked for `response.chats`, which never exists, so every
      // count came back 0 (the empty "0 conversations" filter pills). Match the
      // same `.queue` fallback that useQueue already uses.
      const chats = Array.isArray(response) ? response : ((response as any).queue || response.chats || [])
      const queued = chats.filter((c: any) => c.status === 'queued').length
      const open = chats.filter((c: any) => c.status === 'open').length
      const closed = chats.filter((c: any) => c.status === 'closed').length
      // Missed + archived are fetched separately: a missed chat that was later
      // CLOSED is still "missed" (missed_at IS NOT NULL), but the default "all"
      // query excludes closed/archived, so counting from `chats` here would
      // undercount. The dedicated status=missed query returns ever-missed chats.
      let missed = chats.filter((c: any) => c.status === 'missed' || c.missed_at).length
      try {
        const missedResp = await apiGet<{ chats: any[]; total: number } | any[]>('/queue', { status: 'missed' })
        const missedChats = Array.isArray(missedResp) ? missedResp : ((missedResp as any).queue || missedResp.chats || [])
        missed = missedChats.length
      } catch { /* ignore — fall back to the in-memory count above */ }
      let archived = 0
      try {
        const archivedResp = await apiGet<{ chats: any[]; total: number } | any[]>('/queue', { status: 'archived' })
        const archivedChats = Array.isArray(archivedResp) ? archivedResp : ((archivedResp as any).queue || archivedResp.chats || [])
        archived = archivedChats.length
      } catch { /* ignore */ }
      // Auto Closed is excluded from the default "all" query (closed-like), so
      // fetch its dedicated count like missed/archived.
      let autoClosed = chats.filter((c: any) => c.status === 'auto_closed').length
      try {
        const acResp = await apiGet<{ chats: any[]; total: number } | any[]>('/queue', { status: 'auto_closed' })
        const acChats = Array.isArray(acResp) ? acResp : ((acResp as any).queue || acResp.chats || [])
        autoClosed = acChats.length
      } catch { /* ignore */ }
      setCounts({ all: chats.length, queued, open, closed, missed, auto_closed: autoClosed, archived })
    } catch {
      // Silently fail
    }
  }, [])

  const debouncedFetchCounts = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchCounts(), 300)
  }, [fetchCounts])

  useEffect(() => {
    fetchCounts()
  }, [fetchCounts])

  useEffect(() => {
    if (!socket) return

    const refresh = () => debouncedFetchCounts()

    socket.on('new_chat', refresh)
    socket.on('chat_assigned', refresh)
    socket.on('chat_closed', refresh)
    socket.on('queue_update', refresh)

    return () => {
      socket.off('new_chat', refresh)
      socket.off('chat_assigned', refresh)
      socket.off('chat_closed', refresh)
      socket.off('queue_update', refresh)
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [socket, debouncedFetchCounts])

  return counts
}
